// The standalone Legal app's API client, re-implemented on reception's
// axiosInstance (token attachment + silent refresh live there) with every path
// prefixed by /api/legal. The ported pages call `api.get('/cases')` exactly as
// before - only this file knows they now talk to PrimeOffice.
//
// `currentUser()` keeps the standalone app's synchronous shape
// ({ id, name, role: 'MANAGEMENT' | 'LEGAL_COORDINATOR' }) so the pages' role
// checks are unchanged. The value comes from GET /api/legal/me, loaded once by
// Legal.js before any page renders - Management is the `approve` action on the
// legal-cases card, resolved by the server, never re-derived here.
import axiosInstance from '../../../api/axiosConfig';

const BASE = '/api/legal';

let me = null;
export function setCurrentLegalUser(profile) {
    me = profile ? { id: profile.id, name: profile.name, role: profile.roleName, can: profile.can || {} } : null;
}
export function currentUser() {
    return me;
}
/** True when the caller holds `action` on the legal-cases card (system_admin always). */
export function can(action) {
    return !!me?.can?.[action];
}

export class ApiError extends Error {
    constructor(status, code, message) {
        super(message || code);
        this.status = status;
        this.code = code;
    }
}

function toApiError(err) {
    const res = err?.response;
    if (!res) return new ApiError(0, 'network_error', 'Could not reach the server.');
    const body = res.data && typeof res.data === 'object' && !(res.data instanceof Blob) ? res.data : {};
    return new ApiError(res.status, body.error || 'unknown_error', body.message);
}

async function request(method, path, data) {
    try {
        const res = await axiosInstance.request({ method, url: `${BASE}${path}`, data });
        return res.status === 204 ? undefined : res.data;
    } catch (err) {
        throw toApiError(err);
    }
}

export const api = {
    get: (path) => request('get', path),
    post: (path, body) => request('post', path, body),
    put: (path, body) => request('put', path, body),
    delete: (path) => request('delete', path)
};

// Multipart upload - axios sets the multipart boundary itself for a FormData body.
export async function apiUpload(path, form) {
    try {
        const res = await axiosInstance.post(`${BASE}${path}`, form, { timeout: 120000 });
        return res.data;
    } catch (err) {
        throw toApiError(err);
    }
}

function saveBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// The download route needs the bearer token, so a plain <a href> cannot be
// used - fetch it with auth, then save through a throwaway object URL.
export async function downloadDocument(caseId, docId, fileName) {
    try {
        const res = await axiosInstance.get(`${BASE}/cases/${caseId}/documents/${docId}/download`, { responseType: 'blob', timeout: 120000 });
        saveBlob(res.data, fileName);
    } catch (err) {
        throw toApiError(err);
    }
}

export async function downloadReportExport(type, format, query) {
    const params = new URLSearchParams({ ...query, format });
    try {
        const res = await axiosInstance.get(`${BASE}/reports/${type}/export?${params.toString()}`, { responseType: 'blob', timeout: 120000 });
        const disposition = res.headers?.['content-disposition'] || '';
        const match = disposition.match(/filename="(.+)"/);
        saveBlob(res.data, match?.[1] || `${type}.${format}`);
    } catch (err) {
        throw toApiError(err);
    }
}
